/** Port of flock-expo/src/hooks/useChatQuickSearch.ts (verbatim logic). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/apiClient';
import type { ChatQuickSearchResponse } from '@flock-types/chat.types';

export function useChatQuickSearch(groupId: string, chatGroupId?: string | null) {
  const [result, setResult] = useState<ChatQuickSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  const search = useCallback(
    async (query: string, limit = 20) => {
      const trimmed = query.trim();
      if (!trimmed || !groupId) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      setError(null);
      setResult(null);

      try {
        const body: { query: string; chatGroupId?: string; limit?: number } = {
          query: trimmed,
          limit,
        };
        if (chatGroupId) body.chatGroupId = chatGroupId;

        const response = await api.post<{ success: boolean; data: ChatQuickSearchResponse }>(
          `/groups/${groupId}/chat/quick-search`,
          body
        );

        if (!mountedRef.current || requestIdRef.current !== requestId) return;
        if (response.success && response.data) {
          setResult(response.data);
        } else {
          setError('Search unavailable');
        }
      } catch {
        if (!mountedRef.current || requestIdRef.current !== requestId) return;
        setError('Search unavailable');
      } finally {
        if (mountedRef.current && requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [groupId, chatGroupId]
  );

  return { search, result, loading, error, clear };
}

export default useChatQuickSearch;
